import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';
import { Plus, FileText, Settings, X, Trash2, Pencil, Pin, PinOff, Search, ArrowUpDown, Clock, GripVertical } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const CATEGORY_PATH = {
  archive: '/archive',
  news: '/news',
  random: '/random',
  product: '/product',
};

const TAG_COLORS = ['#818cf8', '#f87171', '#fb923c', '#facc15', '#4ade80', '#22d3ee', '#e879f9', '#f472b6'];

function useTagColor() {
  const { theme } = useTheme();
  return (color) => theme === 'light' ? getContrastColor(color) : 'white';
}

function TagManager({ category, tags, onClose, onRefresh }) {
  const tagColor = useTagColor();
  const [form, setForm] = useState({ name: '', color: TAG_COLORS[0] });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) {
      await api.put(`/posts/tags/${editing.id}`, form);
      setEditing(null);
    } else {
      await api.post('/posts/tags', { ...form, forCategory: category });
    }
    setForm({ name: '', color: TAG_COLORS[0] });
    onRefresh();
  };

  const del = async (id) => {
    if (!confirm('태그를 삭제하시겠습니까?')) return;
    await api.delete(`/posts/tags/${id}`);
    onRefresh();
  };

  const startEdit = (t) => { setEditing(t); setForm({ name: t.name, color: t.color }); };
  const cancelEdit = () => { setEditing(null); setForm({ name: '', color: TAG_COLORS[0] }); };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold">말머리 관리</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>

        <div className="space-y-2 mb-4">
          {tags.map(t => (
            <div key={t.id} className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ backgroundColor: t.color, color: tagColor(t.color) }}>{t.name}</span>
              <div className="flex-1" />
              <button onClick={() => startEdit(t)} className="p-1 text-gray-600 hover:text-white"><Pencil size={12} /></button>
              <button onClick={() => del(t.id)} className="p-1 text-gray-600 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          ))}
          {tags.length === 0 && <p className="text-gray-600 text-sm text-center py-2">말머리가 없습니다.</p>}
        </div>

        <div className="border-t border-gray-800 pt-4 space-y-3">
          <p className="text-gray-400 text-xs">{editing ? '수정' : '새 말머리 추가'}</p>
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="말머리 이름"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
          />
          <div className="flex flex-wrap gap-1.5">
            {TAG_COLORS.map(c => (
              <button
                key={c}
                onClick={() => setForm(f => ({ ...f, color: c }))}
                className={`w-6 h-6 rounded-full border-2 ${form.color === c ? 'border-white' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {editing && <button onClick={cancelEdit} className="flex-1 text-gray-400 text-sm hover:text-white py-2">취소</button>}
            <button onClick={save} className="flex-1 bg-brand text-white text-sm rounded-lg py-2 hover:bg-brand/80">
              {editing ? '수정' : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PostsPage({ category, title }) {
  const tagColor = useTagColor();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showTagMgr, setShowTagMgr] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('priority');
  const dragIdxRef = useRef(null);
  const listRef = useRef(null); // 드래그 시각 피드백용
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';
  const LIMIT = 200; // 정렬 가능하게 전체 로드
  const basePath = CATEGORY_PATH[category] || `/${category}`;
  const isRandom = category === 'random';

  const loadTags = () =>
    api.get('/posts/tags', { params: { forCategory: category } })
      .then(r => setTags(r.data));

  const load = async (p = page, tagId = selectedTag) => {
    const params = { category, page: p, limit: LIMIT, sort };
    if (tagId) params.tagId = tagId;
    if (search) params.search = search;
    const r = await api.get('/posts', { params });
    setPosts(r.data.posts);
    setTotal(r.data.total);
  };

  useEffect(() => {
    setPosts([]); setPage(1); setSelectedTag(null);
    load(1, null);
    loadTags();
  }, [category, sort]);

  // 드래그 정렬 — 모든 시각 피드백도 DOM 직접 조작 (setState 금지)
  const setItemHighlight = (idx, on) => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll('li[data-drag-idx]');
    items.forEach(el => {
      if (parseInt(el.dataset.dragIdx) === idx) {
        el.style.outline = on ? '2px solid var(--brand, #818cf8)' : '';
        el.style.backgroundColor = on ? 'rgba(129,140,248,0.08)' : '';
      }
    });
  };

  const onDragStart = (e, idx) => {
    dragIdxRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    setItemHighlight(idx, true);
  };
  const onDragLeave = (e, idx) => {
    setItemHighlight(idx, false);
  };
  const onDrop = async (e, toIdx) => {
    e.preventDefault();
    setItemHighlight(toIdx, false);
    const fromIdx = dragIdxRef.current;
    dragIdxRef.current = null;
    if (fromIdx === null || fromIdx === toIdx) return;
    const reordered = [...posts];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setPosts(reordered);
    try {
      await api.put('/posts/sort-order', {
        orders: reordered.map((p, i) => ({ id: p.id, sortOrder: i })),
      });
    } catch (err) {
      alert('순서 저장 실패: ' + (err.response?.data?.message || err.message));
      load(1, null);
    }
  };
  const onDragEnd = () => {
    dragIdxRef.current = null;
    if (listRef.current) {
      listRef.current.querySelectorAll('li[data-drag-idx]').forEach(el => {
        el.style.outline = '';
        el.style.backgroundColor = '';
      });
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const startIndex = (page - 1) * LIMIT;

  const fmtDate = (d) => {
    const dt = dayjs(d);
    const now = dayjs();
    if (dt.isSame(now, 'day')) return dt.format('HH:mm');
    if (dt.isSame(now, 'year')) return dt.format('MM.DD');
    return dt.format('YY.MM.DD');
  };

  const selectTag = (tagId) => {
    setSelectedTag(tagId);
    setPage(1);
    load(1, tagId);
  };

  const togglePin = async (e, p) => {
    e.stopPropagation();
    await api.put(`/posts/${p.id}`, { isPinned: !p.isPinned });
    load(page);
  };

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post('/posts', { category, title: '제목 없음', content: '' });
      navigate(`${basePath}/${r.data.id}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <PageHeader icon={FileText} title={title} count={total}>
        {isSuperadmin && tags.length > 0 && (
          <button
            onClick={() => setShowTagMgr(true)}
            className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800"
            title="말머리 관리"
          >
            <Settings size={15} />
          </button>
        )}
        <button
          onClick={create}
          disabled={creating}
          className="flex items-center gap-1.5 bg-brand text-white text-sm px-3 py-1.5 rounded-lg hover:bg-brand/80 disabled:opacity-50"
        >
          <Plus size={14} /> {creating ? '생성 중...' : '작성'}
        </button>
      </PageHeader>

      {/* 검색 + 정렬 (random 카테고리) */}
      {isRandom && (
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 flex-1 min-w-[140px]">
            <Search size={13} className="text-gray-600 flex-shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load(1)}
              placeholder="검색..."
              className="bg-transparent text-white text-sm focus:outline-none flex-1 placeholder-gray-600 min-w-0" />
          </div>
          <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1">
            <button onClick={() => setSort('priority')}
              className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-colors ${sort === 'priority' ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
              <ArrowUpDown size={10} /> 우선순위
            </button>
            <button onClick={() => setSort('date')}
              className={`flex items-center gap-1 px-3 py-1 text-xs rounded-md transition-colors ${sort === 'date' ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>
              <Clock size={10} /> 등록순
            </button>
          </div>
        </div>
      )}
      {isRandom && isSuperadmin && sort === 'priority' && (
        <p className="text-gray-600 text-xs mb-3">✦ 드래그하여 우선순위를 변경할 수 있습니다</p>
      )}

      {/* 말머리 필터 탭 */}
      {tags.length > 0 && (
        <div className="flex items-center gap-1 mb-4 flex-wrap">
          <button
            onClick={() => selectTag(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              !selectedTag ? 'bg-brand text-white' : 'text-gray-400 hover:text-white border border-gray-700'
            }`}
          >
            전체
          </button>
          {tags.map(t => (
            <button
              key={t.id}
              onClick={() => selectTag(t.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedTag === t.id ? '' : 'text-gray-400 hover:text-white border border-gray-700'
              }`}
              style={selectedTag === t.id ? { backgroundColor: t.color, color: tagColor(t.color) } : {}}
            >
              {t.name}
            </button>
          ))}
          {isSuperadmin && (
            <button
              onClick={() => setShowTagMgr(true)}
              className="px-2 py-1 text-gray-600 hover:text-brand text-xs border border-dashed border-gray-700 rounded-full"
            >
              + 말머리 관리
            </button>
          )}
        </div>
      )}
      {tags.length === 0 && isSuperadmin && (
        <div className="mb-4">
          <button
            onClick={() => setShowTagMgr(true)}
            className="text-xs text-gray-600 hover:text-brand border border-dashed border-gray-700 px-3 py-1 rounded-full"
          >
            + 말머리 추가
          </button>
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {/* 테이블 헤더 */}
        <div className={`hidden sm:grid gap-2 px-4 py-2 border-b border-gray-800 text-gray-600 text-xs ${isRandom && isSuperadmin && sort === 'priority' ? 'grid-cols-[2rem_3rem_auto_1fr_7rem_7rem]' : 'grid-cols-[3rem_auto_1fr_7rem_7rem]'}`}>
          {isRandom && isSuperadmin && sort === 'priority' && <span />}
          <span className="text-center">번호</span>
          <span>{tags.length > 0 ? '말머리' : ''}</span>
          <span>제목</span>
          <span>최근수정자</span>
          <span className="text-right">최근수정일</span>
        </div>

        {posts.length === 0 ? (
          <p className="text-gray-600 text-sm text-center py-16">게시글이 없습니다.</p>
        ) : (
          <ul ref={listRef} className="divide-y divide-gray-800">
            {posts.map((p, idx) => {
              // 공지 제외한 우선순위 순번
              const nonPinnedRank = p.isPinned ? null : posts.slice(0, idx).filter(x => !x.isPinned).length;
              return <li
                key={p.id}
                data-drag-idx={idx}
                draggable={isRandom && isSuperadmin && sort === 'priority'}
                onDragStart={isRandom && isSuperadmin && sort === 'priority' ? e => onDragStart(e, idx) : undefined}
                onDragOver={isRandom && isSuperadmin && sort === 'priority' ? e => onDragOver(e, idx) : undefined}
                onDragLeave={isRandom && isSuperadmin && sort === 'priority' ? e => onDragLeave(e, idx) : undefined}
                onDrop={isRandom && isSuperadmin && sort === 'priority' ? e => onDrop(e, idx) : undefined}
                onDragEnd={isRandom && isSuperadmin && sort === 'priority' ? onDragEnd : undefined}
                onClick={() => navigate(`${basePath}/${p.id}`)}
                className={`grid gap-2 items-center px-4 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors group ${
                  isRandom && isSuperadmin && sort === 'priority'
                    ? 'grid-cols-[2rem_3rem_1fr] sm:grid-cols-[2rem_3rem_auto_1fr_7rem_7rem]'
                    : 'grid-cols-[3rem_1fr] sm:grid-cols-[3rem_auto_1fr_7rem_7rem]'
                } ${
                  isRandom && sort === 'priority' && !p.isPinned
                    ? nonPinnedRank === 0 ? 'border-l-2 border-yellow-400 bg-yellow-500/10'
                    : nonPinnedRank === 1 ? 'border-l-2 border-sky-400 bg-sky-500/10'
                    : nonPinnedRank === 2 ? 'border-l-2 border-orange-400 bg-orange-500/10'
                    : ''
                    : ''
                }`}
              >
                {isRandom && isSuperadmin && sort === 'priority' && (
                  <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                    <GripVertical size={14} className="text-gray-700 cursor-grab active:cursor-grabbing" />
                  </div>
                )}
                {/* 번호 / 핀 */}
                <div className="relative flex items-center justify-center">
                  {p.isPinned ? (
                    isSuperadmin ? (
                      <button onClick={e => togglePin(e, p)} title="공지 해제"
                        className="text-[10px] font-bold text-brand hover:opacity-70">공지</button>
                    ) : (
                      <span className="text-[10px] font-bold text-brand">공지</span>
                    )
                  ) : isRandom && sort === 'priority' ? (
                    // 우선순위 모드: 공지 제외 순번 + 메달
                    <div className="flex flex-col items-center gap-0.5">
                      {nonPinnedRank === 0 ? <span className="text-base leading-none">🥇</span>
                        : nonPinnedRank === 1 ? <span className="text-base leading-none">🥈</span>
                        : nonPinnedRank === 2 ? <span className="text-base leading-none">🥉</span>
                        : <span className="text-xs text-gray-600">{nonPinnedRank + 1}</span>
                      }
                      {isSuperadmin && (
                        <button onClick={e => togglePin(e, p)} title="공지로 지정"
                          className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Pin size={9} className="text-gray-600 hover:text-brand" />
                        </button>
                      )}
                    </div>
                  ) : (
                    isSuperadmin ? (
                      <button onClick={e => togglePin(e, p)} title="공지로 지정"
                        className="flex items-center justify-center w-full h-full text-gray-600 hover:text-brand rounded">
                        <Pin size={11} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-600">{total - startIndex - idx}</span>
                    )
                  )}
                </div>

                {/* 말머리 태그 */}
                <span className="hidden sm:block">
                  {p.tag ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
                      style={{ backgroundColor: p.tag.color, color: tagColor(p.tag.color) }}>
                      {p.tag.name}
                    </span>
                  ) : null}
                </span>

                {/* 제목 */}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {p.tag && (
                      <span className="sm:hidden text-[10px] px-1.5 py-0.5 rounded font-medium"
                        style={{ backgroundColor: p.tag.color, color: tagColor(p.tag.color) }}>
                        {p.tag.name}
                      </span>
                    )}
                    <p className={`text-sm font-medium truncate ${p.isPinned ? 'text-white' : 'text-gray-200'}`}>{p.title}</p>
                  </div>
                  <p className="sm:hidden text-xs text-gray-600 mt-0.5">
                    {(p.lastEditor?.name || p.author?.name || p.author?.username)} · {fmtDate(p.updatedAt || p.createdAt)}
                  </p>
                </div>

                {/* 최근수정자 */}
                <span className="hidden sm:block text-xs text-gray-500 truncate">
                  {p.lastEditor?.name || p.author?.name || p.author?.username}
                </span>

                {/* 최근수정일 */}
                <span className="hidden sm:block text-xs text-gray-500 text-right">
                  {fmtDate(p.updatedAt || p.createdAt)}
                </span>
              </li>;
            })}
          </ul>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-1 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => { setPage(p); load(p); }}
              className={`w-8 h-8 text-sm rounded-md ${
                p === page ? 'bg-brand text-white' : 'text-gray-500 hover:text-white hover:bg-gray-800'
              }`}>
              {p}
            </button>
          ))}
        </div>
      )}

      {showTagMgr && (
        <TagManager
          category={category}
          tags={tags}
          onClose={() => setShowTagMgr(false)}
          onRefresh={() => { loadTags(); load(page); }}
        />
      )}
    </div>
  );
}
